import type { ReportResponseDto } from '@api/WhisApi/generated';
import { useReportsFindAll } from '@api/WhisApi/generated';
import type { ReportFiltersValues } from '@components/ReportFilters/reportFilters.types';
import { buildReportFiltersParams } from '@components/ReportFilters/reportFilters.utils';
import { REPORTS_SORT_KEY_MAP } from '@components/ReportList/reportList.constants';
import { SortedColumn } from '@components/ReportList/types/reportList.types';
import { flattenReportPages } from '@components/ReportList/utils/flattenReportPages';
import { REPORTS_PAGE_LIMIT } from '@screens/Reports/reportsScreen.constants';
import { useEffect, useMemo, useRef, useState } from 'react';

interface UseReportsListOptions {
  myReports: boolean;
  pageLimit?: number;
  filters?: ReportFiltersValues;
}

interface UseReportsListResult {
  page: number;
  setPage: (page: number) => void;
  reportPages: Record<number, ReportResponseDto[]>;
  reportCount: number;
  sorting: SortedColumn[];
  hasFetchedReports: boolean;
  hasMore: boolean;
  isPending: boolean;
  isLoadingMore: boolean;
  loadMoreReports: () => void;
  handleSortingChange: (sortedColumns: SortedColumn[]) => void;
}

export const useReportsList = ({
  myReports,
  pageLimit = REPORTS_PAGE_LIMIT,
  filters,
}: UseReportsListOptions): UseReportsListResult => {
  const [page, setPage] = useState(1);
  const [sorting, setSorting] = useState<SortedColumn[]>([]);
  const [reportPages, setReportPages] = useState<
    Record<number, ReportResponseDto[]>
  >({});
  const [reportCount, setReportCount] = useState(0);

  const sortCriteria = useMemo(
    () =>
      sorting.length === 0
        ? []
        : sorting
            .map((sort) => ({
              id: REPORTS_SORT_KEY_MAP[sort.id] ?? sort.id,
              order: sort.order,
            }))
            .filter((sort) => Boolean(sort.id)),
    [sorting],
  );

  const params = useMemo(() => {
    const filterParams = filters ? buildReportFiltersParams(filters) : {};
    const nextParams: Record<
      string,
      string | number | boolean | string[] | undefined
    > = {
      limit: pageLimit,
      page,
      myReports,
      ...filterParams,
    };

    sortCriteria.forEach((sort, index) => {
      nextParams[`sortBy[${index}][id]`] = sort.id;
      nextParams[`sortBy[${index}][order]`] = sort.order;
    });

    return nextParams;
  }, [filters, myReports, page, pageLimit, sortCriteria]);

  const { data: reports, isPending } = useReportsFindAll(params);

  const allReports = useMemo(
    () => flattenReportPages(reportPages),
    [reportPages],
  );
  const hasFetchedReports = Object.keys(reportPages).length > 0;
  const hasMore = allReports.length < reportCount;

  const loadMoreReports = (): void => {
    setPage((previous) => previous + 1);
  };

  const handleSortingChange = (sorted: SortedColumn[]): void => {
    setSorting(sorted);
    setPage(1);
  };

  const filtersKey = useMemo(
    () => (filters ? JSON.stringify(filters) : ''),
    [filters],
  );
  const previousFiltersKey = useRef<string | null>(null);

  useEffect(() => {
    if (
      previousFiltersKey.current &&
      previousFiltersKey.current !== filtersKey
    ) {
      setPage(1);
      setReportPages({});
      setReportCount(0);
    }

    previousFiltersKey.current = filtersKey;
  }, [filtersKey]);

  useEffect(() => {
    if (!reports) {
      return;
    }

    setReportPages((previousPages) => {
      if (page === 1) {
        return { [page]: reports.reports ?? [] };
      }

      return {
        ...previousPages,
        [page]: reports.reports ?? [],
      };
    });
    setReportCount(reports.count);
  }, [page, reports, filtersKey]);

  return {
    page,
    setPage,
    reportPages,
    reportCount,
    sorting,
    hasFetchedReports,
    hasMore,
    isPending,
    isLoadingMore: Boolean(reportPages && isPending),
    loadMoreReports,
    handleSortingChange,
  };
};
